import { HttpClient } from '@angular/common/http';
import { Injectable, Input } from '@angular/core';
import { SessionStorageService } from '@services/storage.service';
import { IReportEmbedConfiguration, models } from 'powerbi-client';
import { BehaviorSubject, catchError, delay, map, Observable, ReplaySubject, retry, Subject, take, takeUntil, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IReport } from './report.service';
import { IAccount, UserService } from './user.service';

interface IEmbedToken {
	token: string;
	expiration: Date;
}

interface IInternalEmbedConfig {
	id: string;
	embedUrl: string;
	accessLevel: string;
	embedToken: IEmbedToken;
}

interface IEmbedConfigInput {
	reportIdGroupId: any;
	impersonateUserId: string;
	impersonateUserPermissions: string[];
}

export interface IEmbedConfig extends IReportEmbedConfiguration {
	groupId: string;
	expiration: Date;
}

@Injectable({
	providedIn: 'root',
})
export class EmbedconfigService {
	public embedConfigs: Array<IEmbedConfig> = [];
	private embedConfigSubject$: BehaviorSubject<IEmbedConfig>;
	public embedConfig$: Observable<IEmbedConfig>;

	public _impersonateUserId?: string;
	@Input() set impersonateUserId(value: string) {
		this.flushAllEmbedConfigs();
		console.log(`flushed all embedconfigs`);
		this._impersonateUserId = value;
	}

	get impersonateUserId(): string {
		return this._impersonateUserId;
	}

	public _impersonateUserPermissions?: string[];
	@Input() set impersonateUserPermissions(value: string[]) {
		this._impersonateUserPermissions = value;
	}

	get impersonateUserPermissions(): string[] {
		return this._impersonateUserPermissions;
	}

	public reportsLoadedSubject = new ReplaySubject<boolean>();
	public reportsLoaded$ = this.reportsLoadedSubject.asObservable();

	private _accountCancel$ = new Subject<void>();

	constructor(
		private http: HttpClient,
		private sessionStorageService: SessionStorageService,
		private userService: UserService,
	) {
		this.embedConfigSubject$ = new BehaviorSubject<IEmbedConfig>(null);
		this.embedConfig$ = this.embedConfigSubject$.asObservable();

		setInterval(() => {
			const isAuth = this.sessionStorageService.getItem('User.Email') !== null && this.sessionStorageService.getItem('User.Email').length > 0;
			if (isAuth) {
				this.refreshExpiredTokens();
			}
		}, 60 * 1000);

		this.userService.accountChanged$.subscribe(() => {
			this._accountCancel$.next();
			this.flushAllEmbedConfigs();
			this.embedConfigSubject$.next(null);
		});
	}

	private refreshExpiredTokens(): void {
		const activeId: string = this.embedConfigSubject$.value?.id;
		const renewConfigs: IReport[] = [];
		this.embedConfigs.forEach((e: IEmbedConfig) => {
			const buffer: number = 5 * 60 * 1000; // Refresh 5 minutes before actual expiration
			const expires: number = new Date(new Date(e.expiration).toUTCString()).getTime();
			const current: number = new Date(new Date(new Date().toUTCString()).getTime() + buffer).getTime();
			if (e) {
				// console.log(`[embedservice][${e.id}]: refresh token in ${((expires - current) / 1000.0)} seconds ... `);
				if (expires < current) {
					// set "|| true" force token renewal every 60 seconds
					console.log(`refreshing expired token (${e.id})`);
					renewConfigs.push({
						externalId: e.id,
						groupId: e.groupId,
					} as IReport);
				}
			}
		});
		if (renewConfigs.length > 0) {
			this.getEmbedConfigBatch('report', renewConfigs, true).subscribe((data) => {
				if (data !== undefined && data.length > 0) {
					data.forEach((e: IEmbedConfig) => {
						this.getLocalEmbedConfig(e.id).accessToken = e.accessToken;
						this.getLocalEmbedConfig(e.id).expiration = e.expiration;
						// If current report's token expired, emit that it updated
						if (activeId === e.id) {
							console.log(`refreshed token for active embedconfig (${activeId})`);
							this.embedConfigSubject$.next(this.getLocalEmbedConfig(e.id));
						} else {
							console.log(`refreshed token for embedconfig (${e.id})`);
						}
					});
				}
			});
		}
	}

	public flushAllEmbedConfigs(): void {
		this.embedConfigs = [];
	}

	public getEmbedConfigBatch(type: string, reports: IReport[], refresh: boolean = false): Observable<IEmbedConfig[]> | undefined {
		const noLocalEmbeds: IReport[] = [];
		reports.forEach((report) => {
			if (!this.getLocalEmbedConfig(report.externalId) || refresh) {
				noLocalEmbeds.push(report);
			}
		});
		if (noLocalEmbeds.length > 0) {
			return this.fetchRemoteEmbedConfigBatch(type, noLocalEmbeds);
		}
		return undefined;
	}

	// Looks in our local embedconfig array and returns the embed if found
	public getLocalEmbedConfig(externalId: string): IEmbedConfig {
		return this.embedConfigs?.find((x) => x.id === externalId);
	}

	// Set the local embedconfig
	// By default this will set the embedconfig as the current embedconfig.
	// The setConfigAsActive can be used if we only wish to add an embedconfig to our local array
	public setLocalEmbedConfig(type: string, externalId: string, groupId: string, setConfigAsActive: boolean = true): void {
		if (this.getLocalEmbedConfig(externalId) && setConfigAsActive) {
			console.log('setting next active embedconfig');
			this.reportsLoadedSubject.next(true);
			this.embedConfigSubject$.next(this.getLocalEmbedConfig(externalId));
			return;
		}
		if (groupId?.length > 0) {
			this.fetchRemoteEmbedConfig(type, externalId, groupId)
				.pipe(take(1))
				.subscribe((f) => {
					if (this.getLocalEmbedConfig(externalId) == null) {
						this.embedConfigs.push(f);
					}
					console.log(`set embedconfig ${externalId} as active: ${setConfigAsActive}`);
					if (setConfigAsActive) {
						console.log('setting next active embedconfig');
						this.reportsLoadedSubject.next(true);
						this.embedConfigSubject$.next(this.getLocalEmbedConfig(externalId));
					}
				});
		} else {
			if (this.getLocalEmbedConfig(externalId) == null) {
				const tomorrow: Date = new Date();
				tomorrow.setDate(tomorrow.getDate() + 1);
				const embedConfig: IEmbedConfig = {
					groupId,
					expiration: tomorrow,
				};
				embedConfig.embedUrl = externalId;
				embedConfig.id = externalId;
				embedConfig.type = 'report';
				embedConfig.accessToken = 'empty';
				this.embedConfigs.push(embedConfig);
				console.log(`set embedconfig ${externalId} as active: ${setConfigAsActive}`);
				if (setConfigAsActive) {
					console.log('setting next active embedconfig');
					this.reportsLoadedSubject.next(true);
					this.embedConfigSubject$.next(this.getLocalEmbedConfig(externalId));
				}
			}
		}
	}

	public addEmbedConfigToArray(embedConf: any) {
		if (this.getLocalEmbedConfig(embedConf.id) == null) {
			console.log(`fetched new obs embedconfig (${embedConf.id})`);
			this.embedConfigs.push(embedConf);
		}
	}

	private removeLocalEmbedConfig(externalId: string) {
		const index = this.embedConfigs?.findIndex((x) => x.id === externalId);
		this.embedConfigs.splice(index, 1);
	}

	// Synchronize the embedconfigs to match the available reports
	public updateLocalEmbedConfig(reports: IReport[]): void {
		this.embedConfigs.forEach((element) => {
			if (reports.findIndex((r) => r.externalId === element.id) === -1) {
				this.removeLocalEmbedConfig(element.id);
			}
		});
	}

	// Get an embedconfig from the backend, used when the embedconfig doesnt exist locally.
	private fetchRemoteEmbedConfig(type: string, externalId: string, groupId: string): Observable<IEmbedConfig> {
		const account: IAccount = JSON.parse(this.sessionStorageService.get('User.Account')) as IAccount;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		const uri = `${environment.awsEnvUri}/insights/embed?${type}Id=${externalId}&groupId=${groupId}${this.impersonateUserId ? `&impersonateUserId=${this.impersonateUserId}` : ''}${this.impersonateUserId ? `&impersonateUserPermissions=${this.impersonateUserPermissions.toString()}` : ''}`;
		return this.http.get<IInternalEmbedConfig>(uri, { headers }).pipe(
			map((val: IInternalEmbedConfig) => ({
				id: externalId,
				groupId,
				type,
				embedUrl: val.embedUrl,
				viewMode: models.ViewMode[val.accessLevel],
				tokenType: models.TokenType.Embed,
				permissions: models.Permissions.All,
				accessToken: val.embedToken.token,
				expiration: val.embedToken.expiration,
			})),
			retry(1),
			catchError(this.handleError),
			takeUntil(this._accountCancel$),
		);
	}

	// Get a batch of embedconfig from the backend, used when the embedconfigs doesnt exist locally.
	private fetchRemoteEmbedConfigBatch(type: string, reports: IReport[]): Observable<IEmbedConfig[]> {
		const account: IAccount = JSON.parse(this.sessionStorageService.get('User.Account')) as IAccount;
		const reportGroupDict = {};
		const uri = `${environment.awsEnvUri}/insights/embed`;
		if (reports === undefined) {
			return undefined;
		}
		reports.forEach((report) => {
			reportGroupDict[report.externalId] = report.groupId;
		});

		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};

		const input: IEmbedConfigInput = {
			reportIdGroupId: reportGroupDict,
			impersonateUserId: this.impersonateUserId ? this.impersonateUserId : '',
			impersonateUserPermissions: this.impersonateUserId ? this.impersonateUserPermissions : [],
		};
		return this.http.post<IInternalEmbedConfig[]>(uri, input, { headers }).pipe(
			map((val: IInternalEmbedConfig[]) => {
				const embedConfigs = [];
				val.forEach((emb) => {
					embedConfigs.push({
						id: emb.id,
						groupId: reportGroupDict[emb.id],
						type,
						embedUrl: emb.embedUrl,
						viewMode: models.ViewMode[emb.accessLevel],
						tokenType: models.TokenType.Embed,
						permissions: models.Permissions.All,
						accessToken: emb.embedToken.token,
						expiration: emb.embedToken.expiration,
					});
				});
				return embedConfigs;
			}),
			delay(2000),
			retry(1),
			catchError(this.handleError),
			takeUntil(this._accountCancel$),
		);
	}

	private handleError(error: { error: { message: string }; status: any; message: any }) {
		const errorMessage: string = error.error instanceof ErrorEvent ? error.error.message : `Error Code: ${error.status}\nMessage: ${error.message}`;
		if (error.status === 403) return [];
		console.log(errorMessage);
		return throwError(() => new Error(errorMessage));
	}
}
